import { mapActivitiesToCalendarEvents } from '@paintscout/util/builder/util/map-activities-to-calendar-events'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  useSaveEventMutation,
  useSearchActivitiesQuery,
  useUnsheduledJobsQuery,
  useSearchUsersQuery
} from '@paintscout/api'
import type { User, EventActivity } from '@paintscout/api'
import type { CalendarEvent } from 'paintscout'
import type { View } from 'react-big-calendar'
import { useLocalStorage } from 'shared/hooks'
import { jsonToLucene } from 'json-to-lucene'
import type { Filter, ValueFilter } from 'json-to-lucene'
import { useSnackbar } from 'notistack'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import { handlePipelineQuery } from 'src/components/pages/deals/DealList/ListView'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util'
import { getCrews } from '@paintscout/util/builder/options/get-crews'
import { Sentry } from 'src/util/appSentry'
import { CALENDAR_BUFFER_DAYS, getViewRange } from '../Calendar/calendarUtils'
import type { ZoomLevel } from './TimelineHeader'
import { filterScheduledEvents } from './scheduledEventFilters'

interface ProductionCalendarContextValue {
  events: CalendarEvent[]
  setEvents: (events: CalendarEvent[]) => void
  visibleEvents: CalendarEvent[]
  unscheduledEvents: CalendarEvent[]
  totalUnscheduledEvents: number
  fetchMoreUnscheduledEvents: () => Promise<any>
  scheduledFilters: {
    scheduledSelectedProjectTypes: string[] | null
    setScheduledSelectedProjectTypes: (types: string[] | null) => void
    scheduledSelectedProjectManagers: string[]
    setScheduledSelectedProjectManagers: (managers: string[]) => void
    scheduledSelectedSalesReps: string[]
    setScheduledSelectedSalesReps: (salesReps: string[]) => void
    scheduledMinPrice: number | null
    scheduledMaxPrice: number | null
    setScheduledMinPrice: (price: string) => void
    setScheduledMaxPrice: (price: string) => void
  }
  unscheduledFilters: {
    unscheduledSelectedProjectTypes: string[] | null
    setUnscheduledSelectedProjectTypes: (types: string[] | null) => void
    unscheduledMinPrice: number | null
    unscheduledMaxPrice: number | null
    setUnscheduledMinPrice: (price: string) => void
    setUnscheduledMaxPrice: (price: string) => void
    unscheduledMinHours: number | null
    unscheduledMaxHours: number | null
    setUnscheduledMinHours: (hours: string) => void
    setUnscheduledMaxHours: (hours: string) => void
    unscheduledSelectedProjectManagers: string[]
    setUnscheduledSelectedProjectManagers: (stages: string[]) => void
    unscheduledSelectedStages: string[]
    setUnscheduledSelectedStages: (stages: string[]) => void
    unscheduledTags: string[]
    setUnscheduledTags: (tags: string[]) => void
  }
  date: Date
  setDate: (date: Date) => void
  targetDate: Date
  setTargetDate: (date: Date) => void
  startDate: Date
  endDate: Date
  setVisibleWindow: (targetDate: Date) => void
  zoomLevel: ZoomLevel
  setZoomLevel: (zoomLevel: ZoomLevel) => void
  monthColumns: number
  setMonthColumns: (count: number) => void
  quarterColumns: number
  setQuarterColumns: (count: number) => void
  view: View
  setView: (view: View) => void
  tab: 'calendar' | 'map'
  loading: boolean
  hideWeekends: boolean
  setHideWeekends: (hide: boolean) => void
  selectedUsers?: User[]
  setSelectedUsers?: (users: User[]) => void

  activeProjectBlockId: string | null
  setActiveProjectBlockId: (id: string | null) => void

  projectManagerOrder: string[]
  setProjectManagerOrder: (order: string[]) => void

  updateEvent: (event: CalendarEvent) => void
  deleteEvent: (event: CalendarEvent) => void
}

const ProductionCalendarContext = createContext<ProductionCalendarContextValue | undefined>(undefined)

// Lucene filter prefix for the scheduled-events query: production events
// that are not trashed or canceled.
const SCHEDULED_BASE_FILTERS: Filter[] = [
  { id: 'details.activityType', type: 'equal', value: 'event' } as Filter,
  { id: 'details.production', type: 'equal', value: true } as Filter,
  { id: 'trashed', type: 'equal', value: true, not: true } as Filter,
  { id: 'details.canceled', type: 'equal', value: true, not: true } as Filter
]

export const ProductionCalendarProvider: React.FC<{ children: React.ReactNode; tab?: 'calendar' | 'map' }> = ({
  children,
  tab = 'calendar'
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { isSuperadmin } = useUser()
  const { clientId, options } = useClientOptions()
  const crews = getCrews({ options, includeTrashed: true })
  const [view, setView] = useLocalStorage<View>('productionCalendarView', 'day')
  const [hideWeekends, setHideWeekendsState] = useState(
    () => localStorage.getItem('productionCalendarHideWeekends') === 'true'
  )
  const setHideWeekends = useCallback((value: boolean) => {
    localStorage.setItem('productionCalendarHideWeekends', value ? 'true' : 'false')
    setHideWeekendsState(value)
  }, [])
  const [saveEvent] = useSaveEventMutation()
  const [loading, setLoading] = useState(true)
  const [date, setDate] = useState<Date>(new Date())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const eventsRef = useRef<CalendarEvent[]>([])
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const [unscheduledEvents, setUnscheduledEvents] = useState<CalendarEvent[]>([])
  const unscheduledEventsRef = useRef<CalendarEvent[]>([])
  const [totalUnscheduledEvents, setTotalUnscheduledEvents] = useState<number>(0)
  const usersQuery = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''

  // Single source of truth for the target date - now as state
  // Initialize with today minus 2 days so today appears as the 3rd visible day
  const [targetDate, setTargetDate] = useState(() => {
    const today = new Date(Date.now())
    today.setDate(today.getDate() - 2) // Start 2 days before today
    return today
  })

  // Calculate timeline range based on zoom level
  // const startDate = new Date(targetDate)
  // startDate.setHours(0, 0, 0, 0) // Normalize to midnight local time
  // const endDate = (() => {
  //   const d = new Date(startDate)
  //   d.setDate(d.getDate() + 29)
  //   return d
  // })()

  const BUFFER_DAYS = 30 // or whatever makes sense for your use case

  const [visibleStartDate, setVisibleStartDate] = useState<Date>(() => {
    const date = new Date(targetDate)
    date.setHours(0, 0, 0, 0)
    return date
  })
  const [visibleEndDate, setVisibleEndDate] = useState<Date>(() => {
    const d = new Date(visibleStartDate)
    d.setDate(d.getDate() + 29)
    return d
  })
  const [fetchedStartDate, setFetchedStartDate] = useState<Date>(
    new Date(visibleStartDate.getTime() - BUFFER_DAYS * 86400000)
  )
  const [fetchedEndDate, setFetchedEndDate] = useState<Date>(
    new Date(visibleEndDate.getTime() + BUFFER_DAYS * 86400000)
  )

  const [scheduledSelectedProjectManagers, setScheduledSelectedProjectManagers] = useLocalStorage<string[]>(
    'productionCalendarScheduledProjectManagers',
    []
  )
  const [scheduledSelectedProjectTypes, setScheduledSelectedProjectTypes] = useLocalStorage<string[]>(
    'productionCalendarScheduledProjectTypes',
    []
  )
  const [scheduledSelectedSalesReps, setScheduledSelectedSalesReps] = useLocalStorage<string[]>(
    'productionCalendarScheduledSalesReps',
    []
  )
  const [scheduledMinPrice, setScheduledMinPrice] = useLocalStorage<string>('productionCalendarScheduledMinPrice', '')
  const [scheduledMaxPrice, setScheduledMaxPrice] = useLocalStorage<string>('productionCalendarScheduledMaxPrice', '')
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('days')
  const [activeProjectBlockId, setActiveProjectBlockId] = useState<string | null>(null)
  const [projectManagerOrder, setProjectManagerOrder] = useLocalStorage<string[]>(
    'productionCalendarProjectManagerOrder',
    []
  )
  const [monthColumns, setMonthColumns] = useState(12)
  const [quarterColumns, setQuarterColumns] = useState(20)
  const filters = [
    ...SCHEDULED_BASE_FILTERS,
    ...(scheduledSelectedSalesReps && scheduledSelectedSalesReps.length
      ? [
          {
            id: 'details.owner.id',
            type: 'in',
            // both auth0 and non-auth0 ids
            value: scheduledSelectedSalesReps.flatMap((id) => [addAuth0Prefix(id), removeAuth0Prefix(id)])
          } as Filter
        ]
      : []),
    ...(scheduledMinPrice || scheduledMaxPrice
      ? [
          {
            id: 'details.deal.total',
            type: 'range',
            value: [
              scheduledMinPrice ? parseFloat(scheduledMinPrice) : undefined,
              scheduledMaxPrice ? parseFloat(scheduledMaxPrice) : undefined
            ],
            includeNull: true
          } as Filter
        ]
      : []),
    {
      id: 'details.end',
      type: 'greaterThan',
      value: fetchedStartDate.getTime()
    } as Filter,
    {
      id: 'details.start',
      type: 'lessThan',
      value: fetchedEndDate.getTime()
    } as Filter
  ]

  const rangeFromZoomLevel = (zoomLevel: ZoomLevel, date: Date): Date => {
    const newDate = new Date(date)
    switch (zoomLevel) {
      case 'days':
        // newDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate())
        newDate.setDate(newDate.getDate() + 29)
        break
      case 'weeks':
        // newDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate())
        newDate.setDate(date.getDate() + 28 * 7 - 1) // 28 weeks
        break
      case 'months':
        newDate.setMonth(date.getMonth() + Math.max(1, monthColumns) - 1)
        break
      case 'quarters':
        newDate.setMonth(date.getMonth() + Math.max(1, quarterColumns) * 3 - 3)
        break
    }
    return newDate
  }

  // Function to update the visible window and fetch if needed
  const setVisibleWindow = (targetDate: Date) => {
    const newStart = new Date(targetDate)
    newStart.setHours(0, 0, 0, 0)
    const newEnd = rangeFromZoomLevel(zoomLevel, newStart)
    setTargetDate(targetDate)
    setVisibleStartDate(newStart)
    setVisibleEndDate(newEnd)
    // If the new window is outside the fetched buffer, expand and fetch
    if (newStart < fetchedStartDate || newEnd > fetchedEndDate) {
      const expandedStart = new Date(newStart.getTime() - BUFFER_DAYS * 86400000)
      const expandedEnd = new Date(newEnd.getTime() + BUFFER_DAYS * 86400000)
      setFetchedStartDate(expandedStart)
      setFetchedEndDate(expandedEnd)
    }
  }

  useEffect(() => {
    const newStart = new Date(targetDate)
    newStart.setHours(0, 0, 0, 0)
    const newEnd = rangeFromZoomLevel(zoomLevel, newStart)
    setVisibleStartDate(newStart)
    setVisibleEndDate(newEnd)

    if (newStart < fetchedStartDate || newEnd > fetchedEndDate) {
      const expandedStart = new Date(newStart.getTime() - BUFFER_DAYS * 86400000)
      const expandedEnd = new Date(newEnd.getTime() + BUFFER_DAYS * 86400000)
      setFetchedStartDate(expandedStart)
      setFetchedEndDate(expandedEnd)
    }
  }, [zoomLevel, monthColumns, quarterColumns, targetDate])

  // For day/week/month calendar views, expand the fetch window when the user navigates.
  // Agenda view is handled separately via setVisibleWindow / targetDate.
  // fetchedStartDate/fetchedEndDate are in deps so the check uses the latest window values;
  // expansion is idempotent so this doesn't cause infinite loops.
  useEffect(() => {
    if (view === 'agenda') return
    const { start, end } = getViewRange(date, view)
    if (start.getTime() < fetchedStartDate.getTime() || end.getTime() > fetchedEndDate.getTime()) {
      setFetchedStartDate(new Date(start.getTime() - CALENDAR_BUFFER_DAYS * 86400000))
      setFetchedEndDate(new Date(end.getTime() + CALENDAR_BUFFER_DAYS * 86400000))
    }
  }, [date, view, fetchedStartDate, fetchedEndDate])

  const { loading: loadingJobs } = useSearchActivitiesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: false,
    variables: {
      query: jsonToLucene({ filters }),
      limit: null,
      isProductionCalendar: true
    },
    onCompleted: (data) => {
      // Transform data.activities into CalendarEvent format
      const transformedEvents = mapActivitiesToCalendarEvents(
        data.searchActivities.activities as EventActivity[],
        selectedUsers,
        false
      )
      setEvents(transformedEvents)
    },
    onError: useCallback((error) => {
      console.error(error)
      enqueueSnackbar('Something went wrong, please try again later.', {
        variant: 'error',
        autoHideDuration: 3000
      })
      Sentry.captureException(error)
    }, [])
  })

  // -- Unscheduled Events Query and State -- //
  const [unscheduledSelectedProjectManagers, setUnscheduledSelectedProjectManagers] = useLocalStorage<string[]>(
    'productionCalendarUnscheduledProjectManagers',
    []
  )
  const [unscheduledSelectedProjectTypes, setUnscheduledSelectedProjectTypes] = useLocalStorage<string[]>(
    'productionCalendarUnscheduledProjectTypes',
    []
  )
  const [unscheduledSelectedStages, setUnscheduledSelectedStages] = useLocalStorage<string[]>(
    'productionCalendarUnscheduledStages',
    []
  )
  const [unscheduledTags, setUnscheduledTags] = useLocalStorage<string[]>('productionCalendarUnscheduledTags', [])
  const [unscheduledMinPrice, setUnscheduledMinPrice] = useLocalStorage<string>(
    'productionCalendarUnscheduledMinPrice',
    ''
  )
  const [unscheduledMaxPrice, setUnscheduledMaxPrice] = useLocalStorage<string>(
    'productionCalendarUnscheduledMaxPrice',
    ''
  )
  const [unscheduledMinHours, setUnscheduledMinHours] = useLocalStorage<string>(
    'productionCalendarUnscheduledMinHours',
    ''
  )
  const [unscheduledMaxHours, setUnscheduledMaxHours] = useLocalStorage<string>(
    'productionCalendarUnscheduledMaxHours',
    ''
  )

  const unscheduledQuery = handlePipelineQuery({
    pipeline: 'production-pipeline',
    filters: [
      ...(unscheduledSelectedProjectTypes.length
        ? [
            {
              id: 'quoteType',
              type: 'in',
              value: unscheduledSelectedProjectTypes
            } as ValueFilter
          ]
        : []),
      ...(unscheduledSelectedProjectManagers.length
        ? [
            {
              id: 'owner.id',
              type: 'in',
              value: unscheduledSelectedProjectManagers
            } as ValueFilter
          ]
        : []),
      ...(unscheduledSelectedStages.length
        ? [
            {
              id: 'stage.value',
              type: 'in',
              value: unscheduledSelectedStages
            } as ValueFilter
          ]
        : []),
      ...(unscheduledTags.length
        ? [
            {
              id: 'tags',
              type: 'in',
              value: unscheduledTags
            } as ValueFilter
          ]
        : []),
      ...(unscheduledMinPrice || unscheduledMaxPrice
        ? [
            {
              id: 'value',
              type: 'range',
              value: [
                unscheduledMinPrice ? parseFloat(unscheduledMinPrice) : undefined,
                unscheduledMaxPrice ? parseFloat(unscheduledMaxPrice) : undefined
              ],
              includeNull: true
            } as ValueFilter
          ]
        : []),
      ...(unscheduledMinHours
        ? [
            {
              id: 'hours',
              type: 'range',
              value: [parseFloat(unscheduledMinHours), undefined]
            } as ValueFilter
          ]
        : []),
      ...(unscheduledMaxHours
        ? [
            {
              id: 'hours',
              type: 'range',
              value: [undefined, parseFloat(unscheduledMaxHours)]
            } as ValueFilter
          ]
        : [])
    ],
    query: null
  })
  // Tracks the latest pagination cursor returned from the server. Held in a
  // ref so `fetchMore` reads the current cursor even when fired from a
  // still-attached scroll handler whose closure has not yet seen the post-fetch
  // re-render — otherwise the same bookmark can be sent twice, producing
  // duplicate activities in the merged cache.
  const unscheduledBookmarkRef = useRef<string | null>(null)
  const {
    data: unscheduledJobsData,
    fetchMore,
    refetch
  } = useUnsheduledJobsQuery({
    variables: { query: unscheduledQuery, limit: 8 },
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (!unscheduledJobsData) return
    const transformedEvents = mapActivitiesToCalendarEvents(
      unscheduledJobsData.unscheduledJobs.activities as EventActivity[],
      selectedUsers,
      true
    )
    // Anything in the unscheduled list must not carry prior scheduling fields —
    // otherwise the Schedule dialog (and drag handler) pre-fill from the
    // previous schedule when a deleted job round-trips back through here.
    const cleanedEvents = transformedEvents.map((e) => ({
      ...e,
      start: null,
      end: null,
      crew: null,
      assignedUsers: undefined,
      progress: 0,
      previousProgress: 0
    }))
    // Filter out any events already on the timeline to prevent a stale server
    // response from restoring a job that was just scheduled locally.
    const scheduledIds = new Set(eventsRef.current.map((e) => e.id))
    const filteredEvents = cleanedEvents.filter((e) => !scheduledIds.has(e.id))
    const filteredOutCount = cleanedEvents.length - filteredEvents.length
    unscheduledBookmarkRef.current = unscheduledJobsData.unscheduledJobs.bookmark
    setTotalUnscheduledEvents(unscheduledJobsData.unscheduledJobs.total_rows - filteredOutCount)
    setUnscheduledEvents(filteredEvents)
  }, [unscheduledJobsData])

  useEffect(() => {
    unscheduledEventsRef.current = unscheduledEvents
  }, [unscheduledEvents])

  useEffect(() => {
    eventsRef.current = events
  }, [events])

  useSearchUsersQuery({
    variables: {
      query: usersQuery,
      includeInactive: true
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const users = data?.searchUsers?.rows ?? []
      setSelectedUsers(users)
      setLoading(false)
    }
  })

  // Wrap fetchMore to match the expected signature
  const fetchMoreUnscheduledEvents = () => {
    return fetchMore({
      variables: {
        bookmark: unscheduledBookmarkRef.current,
        limit: 8,
        query: unscheduledQuery
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        // Dedupe by id: a stale-bookmark re-fetch or shifting cursor on the
        // server can otherwise concatenate the same activity twice.
        const prevActivities = (prev.unscheduledJobs.activities ?? []) as EventActivity[]
        const nextActivities = (fetchMoreResult.unscheduledJobs.activities ?? []) as EventActivity[]
        const seen = new Set(prevActivities.map((a) => a._id))
        const additions = nextActivities.filter((a) => !seen.has(a._id))
        return {
          ...fetchMoreResult,
          unscheduledJobs: {
            ...fetchMoreResult.unscheduledJobs,
            activities: [...prevActivities, ...additions]
          }
        }
      }
    })
  }

  // Surface a save/delete failure to the user and Sentry. Callers are
  // responsible for the matching state rollback before invoking this.
  const handleMutationError = (error: unknown, message: string) => {
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: 4000 })
    Sentry.captureException(error)
  }

  const updateEvent = async (updatedEvent: CalendarEvent) => {
    const isNowScheduled = !!updatedEvent.start
    const wasUnscheduled = unscheduledEventsRef.current.some((e) => e.id === updatedEvent.id)
    // Snapshot prior state for rollback if the save fails.
    const prevScheduled = eventsRef.current.find((e) => e.id === updatedEvent.id)
    const prevUnscheduled = unscheduledEventsRef.current.find((e) => e.id === updatedEvent.id)

    // Idempotent upsert across both lists + their refs; passing `undefined` removes the event from that list.
    const applyState = (scheduled?: CalendarEvent, unscheduled?: CalendarEvent) => {
      const upsert = (arr: CalendarEvent[], item?: CalendarEvent) => {
        const without = arr.filter((e) => e.id !== updatedEvent.id)
        return item ? [...without, item] : without
      }
      setEvents((prev) => upsert(prev, scheduled))
      setUnscheduledEvents((prev) => upsert(prev, unscheduled))
      eventsRef.current = upsert(eventsRef.current, scheduled)
      unscheduledEventsRef.current = upsert(unscheduledEventsRef.current, unscheduled)
    }

    applyState(isNowScheduled ? updatedEvent : undefined, isNowScheduled ? undefined : updatedEvent)
    if (wasUnscheduled && isNowScheduled) setTotalUnscheduledEvents((t) => t - 1)
    else if (!wasUnscheduled && !isNowScheduled) setTotalUnscheduledEvents((t) => t + 1)

    try {
      await saveEvent({ variables: { event: updatedEvent } })
    } catch (error) {
      applyState(prevScheduled, prevUnscheduled)
      if (wasUnscheduled && isNowScheduled) setTotalUnscheduledEvents((t) => t + 1)
      else if (!wasUnscheduled && !isNowScheduled) setTotalUnscheduledEvents((t) => t - 1)
      handleMutationError(error, 'Could not update event, please try again.')
    }
  }

  const deleteEvent = async (event: CalendarEvent) => {
    const previousEvent = eventsRef.current.find((e) => e.id === event.id)
    setEvents((prevEvents) => prevEvents.filter((prevEvent) => prevEvent.id !== event.id))
    try {
      await saveEvent({
        variables: {
          event: { ...event, canceled: true }
        }
      })
      refetch()
    } catch (error) {
      if (previousEvent) {
        setEvents((prev) => [...prev, previousEvent])
      }
      handleMutationError(error, 'Could not delete event, please try again.')
    }
  }

  const filteredScheduledEvents = filterScheduledEvents({
    events,
    crews,
    filters: {
      selectedProjectTypes: scheduledSelectedProjectTypes,
      selectedSalesReps: scheduledSelectedSalesReps,
      selectedProjectManagers:
        view === 'agenda' ? scheduledSelectedProjectManagers : selectedUsers.map((u) => removeAuth0Prefix(u.user_id)),
      minPrice: scheduledMinPrice,
      maxPrice: scheduledMaxPrice
    }
  })

  return (
    <ProductionCalendarContext.Provider
      value={{
        date,
        setDate,
        targetDate,
        setTargetDate,
        startDate: visibleStartDate,
        endDate: visibleEndDate,
        setVisibleWindow,
        zoomLevel,
        setZoomLevel,
        monthColumns,
        setMonthColumns,
        quarterColumns,
        setQuarterColumns,
        view,
        setView,
        tab,
        loading: loading || loadingJobs,
        hideWeekends,
        setHideWeekends,
        events: filteredScheduledEvents,
        setEvents,
        visibleEvents: filteredScheduledEvents.filter((event) => {
          // Only show events that are within the visible window
          return event.start && event.end && event.start < visibleEndDate && event.end > visibleStartDate
        }),
        unscheduledEvents,
        totalUnscheduledEvents,
        fetchMoreUnscheduledEvents: fetchMore ? fetchMoreUnscheduledEvents : null,
        scheduledFilters: {
          scheduledSelectedProjectTypes: scheduledSelectedProjectTypes || null,
          setScheduledSelectedProjectTypes: (types: string[] | null) => setScheduledSelectedProjectTypes(types || []),
          scheduledSelectedProjectManagers,
          setScheduledSelectedProjectManagers: (managers: string[]) => setScheduledSelectedProjectManagers(managers),
          scheduledSelectedSalesReps: scheduledSelectedSalesReps || [],
          setScheduledSelectedSalesReps: (salesReps: string[]) => setScheduledSelectedSalesReps(salesReps),
          scheduledMinPrice: scheduledMinPrice ? Number(scheduledMinPrice) : null,
          scheduledMaxPrice: scheduledMaxPrice ? Number(scheduledMaxPrice) : null,
          setScheduledMinPrice: (price: string) => setScheduledMinPrice(price ? String(price) : ''),
          setScheduledMaxPrice: (price: string) => setScheduledMaxPrice(price ? String(price) : '')
        },
        unscheduledFilters: {
          unscheduledSelectedProjectTypes: unscheduledSelectedProjectTypes || null,
          setUnscheduledSelectedProjectTypes: (types: string[] | null) =>
            setUnscheduledSelectedProjectTypes(types || []),
          unscheduledSelectedProjectManagers,
          setUnscheduledSelectedProjectManagers: (managers: string[]) =>
            setUnscheduledSelectedProjectManagers(managers),
          unscheduledMinPrice: unscheduledMinPrice ? Number(unscheduledMinPrice) : null,
          unscheduledMaxPrice: unscheduledMaxPrice ? Number(unscheduledMaxPrice) : null,
          setUnscheduledMinPrice: (price: string) => setUnscheduledMinPrice(price ? String(price) : ''),
          setUnscheduledMaxPrice: (price: string) => setUnscheduledMaxPrice(price ? String(price) : ''),
          unscheduledMinHours: unscheduledMinHours ? Number(unscheduledMinHours) : null,
          unscheduledMaxHours: unscheduledMaxHours ? Number(unscheduledMaxHours) : null,
          setUnscheduledMinHours: (hours: string) => setUnscheduledMinHours(hours ? String(hours) : ''),
          setUnscheduledMaxHours: (hours: string) => setUnscheduledMaxHours(hours ? String(hours) : ''),
          unscheduledSelectedStages: unscheduledSelectedStages || [],
          setUnscheduledSelectedStages: (stages: string[]) => setUnscheduledSelectedStages(stages),
          unscheduledTags: unscheduledTags || [],
          setUnscheduledTags: (tags: string[]) => setUnscheduledTags(tags)
        },
        selectedUsers,
        setSelectedUsers,
        activeProjectBlockId,
        setActiveProjectBlockId,
        projectManagerOrder,
        setProjectManagerOrder,
        updateEvent,
        deleteEvent
      }}
    >
      {children}
    </ProductionCalendarContext.Provider>
  )
}

export const useProductionCalendar = (): ProductionCalendarContextValue => {
  const context = useContext(ProductionCalendarContext)
  if (!context) {
    throw new Error('useProductionCalendar must be used within a ProductionCalendarProvider')
  }
  return context
}
